import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  MarkEmailRead as MarkEmailReadIcon,
  AccountCircle as AccountCircleIcon,
  MeetingRoom as MeetingIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Profile_page } from '../navigations/path';
import { getNotifications, markNotificationAsRead } from '../store/slices/userSlice';
import { toast } from 'react-toastify';

const Navbar = () => {
  const dispatch = useDispatch();
  const { user, logout } = useAuth();
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch notifications on component mount and set up interval
  useEffect(() => {
    fetchUnreadNotifications();

    // const interval = setInterval(() => {
    //   fetchUnreadNotifications();
    // }, 5000);

    // return () => clearInterval(interval);
  }, []);

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };

  // Notification menu handlers
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    fetchUnreadNotifications(true);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  // Fetch unread notifications
  const fetchUnreadNotifications = async (isMenuOpen = false) => {
    try {
      if (isMenuOpen) {
        setNotificationLoading(true);
      } else {
        setLoading(true);
      }

      const response = await dispatch(getNotifications()).unwrap();
      const data = response?.data ?? response;
      if (data) {
        setNotifications(data?.unread ?? data?.data?.unread ?? []);
        setTotalUnread(data?.total_unread ?? data?.data?.total_unread ?? 0);
      } else {
        setNotifications([]);
        setTotalUnread(0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      if (isMenuOpen) {
        toast.error(error.message || 'Failed to load notifications');
      }
      setNotifications([]);
      setTotalUnread(0);
    } finally {
      if (isMenuOpen) {
        setNotificationLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleProfileClick = () => {
    navigate(Profile_page);
    handleUserMenuClose();
  };

  // UPDATED: Mark single notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await dispatch(markNotificationAsRead(notificationId)).unwrap();

      // Remove from local state and update count
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      setTotalUnread(prev => prev - 1);
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format notification time
  const formatTime = (dateString) => {
    if (!dateString) return 'Recently';

    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (notificationType) => {
    switch (notificationType) {
      case 'meeting_scheduled':
        return <MeetingIcon sx={{ color: '#154A8A', fontSize: 20 }} />;
      default:
        return <EmailIcon sx={{ color: '#154A8A', fontSize: 20 }} />;
    }
  };

  // UPDATED: Handle notification click with mark as read and navigation to lead detail
  const handleNotificationClick = async (notification) => {
    try {
      // First mark the notification as read
      await dispatch(markNotificationAsRead(notification.id)).unwrap();

      // Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== notification.id));
      setTotalUnread(prev => prev - 1);

      // Extract UUID from the URL and navigate to lead detail
      if (notification.data?.url) {
        const url = notification.data.url;

        // Check if it's a lead URL and extract UUID
        if (url.includes('/leads/')) {
          // Extract the UUID from the URL
          const urlParts = url.split('/');
          const leadUuid = urlParts[urlParts.length - 1];


          // This matches your route: /leads/view/:id
          navigate(`/leads/view/${leadUuid}`);
        } else if (url.includes('/issues/')) {
          const urlParts = url.split('/');
          const leadUuid = urlParts[urlParts.length - 1];

          navigate(`/issues/view/${leadUuid}`);
        }

        else {
          // If it's not a lead URL, you can handle other types of notifications here
          // You might want to navigate to a default page or show a message
          // navigate('/dashboard');
        }
      } else {
      }

      handleNotificationMenuClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Failed to process notification');
    }
  };

  return (
    <Box>
      {/* Top Orange Strip */}
      <Box
        sx={{
          height: 4,
          bgcolor: '#FF8C42',
          width: '100%'
        }}
      />

      {/* Main Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'grey.200',
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
        {/* Center - Logo and Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img style={{ width: '100%', height: '80px' }} src="/images/revive-portal-logo.png" alt="Revive Portal" />
          </Box>
        </Box>

        {/* Right Side - Notifications and User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Notifications Bell Icon */}
          <IconButton
            onClick={handleNotificationMenuOpen}
            sx={{
              color: '#154A8A',
              '&:hover': {
                backgroundColor: 'rgba(21, 74, 138, 0.04)'
              }
            }}
          >
            <Badge
              badgeContent={totalUnread}
              color="error"
              max={99}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Info with Avatar and Name */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(21, 74, 138, 0.04)'
              }
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#154A8A',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {getUserInitials()}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                color: '#154A8A',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {user?.name || 'User'}
            </Typography>
          </Box>
        </Box>

        {/* Notifications Dropdown Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 360,
              maxHeight: 480,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              mt: 1
            }
          }}
        >
          {/* Notifications Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'grey.200' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: '#154A8A', fontWeight: 600 }}>
                Notifications
                {totalUnread > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      color: 'error.main',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    ({totalUnread} unread)
                  </Typography>
                )}
              </Typography>
              {notifications.length > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  // onClick={handleMarkAllAsRead}
                  sx={{
                    color: '#154A8A',
                    fontSize: '0.75rem',
                    textTransform: 'none'
                  }}
                >
                  Mark all as read
                </Button>
              )}
            </Box>
          </Box>

          {/* Notifications List */}
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notificationLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} sx={{ color: '#154A8A' }} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'grey.100',
                      '&:last-child': { borderBottom: 0 },
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notification.data?.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {notification.data?.title || 'New Notification'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {notification.data?.message || 'You have a new notification'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {formatTime(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      sx={{ color: 'grey.500' }}
                    >
                      <MarkEmailReadIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Notifications Footer */}
          {notifications.length > 0 && (
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'grey.200', textAlign: 'center' }}>
              <Button
                size="small"
                sx={{
                  color: '#154A8A',
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
              >
                View All Notifications
              </Button>
            </Box>
          )}
        </Menu>

        {/* User Dropdown Menu */}
        <Menu
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 200,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              mt: 1
            }
          }}
        >
          {/* User Info in Dropdown */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ color: '#154A8A', fontWeight: 600 }}>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || 'user@example.com'}
            </Typography>
          </Box>

          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" sx={{ color: '#154A8A' }} />
            </ListItemIcon>
            <Typography variant="body2">Profile</Typography>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <Typography variant="body2" color="error">Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Navbar;